import Decimal from 'decimal.js';
import * as yup from 'yup';

import getCurrencyMaxDecimals from 'modules/menu/components/getCurrencyMaxDecimals';

import { PriceChangeFormValues } from './BulkPriceChangeModal';

export const yupTestUpToDecimalPlaces = (decimals: number) => ({
  name: 'upToDecimalPlaces',
  message:
    decimals === 1 ? { id: 'validation.atMostOneDecimal' } : { id: 'validation.atMostNDecimals', values: { decimals } },
  test: (value: number | undefined) => value === undefined || new Decimal(value).decimalPlaces() <= decimals,
});

export const yupTestMonetaryDecimals = {
  name: 'monetaryDecimals',
  test: (value: number | undefined, context: yup.TestContext<{ currency: Currency }>) => {
    const decimals = context.options.context?.currency ? getCurrencyMaxDecimals(context.options.context.currency) : 2;

    if (value !== undefined && new Decimal(value).decimalPlaces() > decimals) {
      return context.createError({
        message:
          decimals === 0
            ? { id: 'validation.mustBeInteger' }
            : { id: 'validation.atMostNDecimals', values: { decimals } },
      });
    }
    return true;
  },
};

export const priceChangeValidationSchema = yup.object({
  type: yup
    .mixed<PriceChangeFormValues['type']>()
    .oneOf<
      PriceChangeFormValues['type']
    >(['singlePrice', 'changeByAmount', 'changeByPercentage', 'changeByDiscountAmount', 'changeByDiscountPercentage'])
    .required(),
  operation: yup
    .mixed<PriceChangeFormValues['operation']>()
    .oneOf<PriceChangeFormValues['operation']>(['addition', 'deduction'])
    .required(),
  singlePrice: yup
    .mixed<number | string | undefined>()
    .when('type', (type: PriceChangeFormValues['type'], schema: yup.BaseSchema) =>
      type === 'singlePrice' ? yup.number().min(0).test(yupTestMonetaryDecimals).required() : schema,
    ),
  changeAmount: yup
    .mixed<number | string | undefined>()
    .when('type', (type: PriceChangeFormValues['type'], schema: yup.BaseSchema) =>
      type === 'changeByAmount' ? yup.number().min(0).test(yupTestMonetaryDecimals).required() : schema,
    ),
  applyDiscountAmount: yup
    .mixed<number | string | undefined>()
    .when('type', (type: PriceChangeFormValues['type'], schema: yup.BaseSchema) =>
      type === 'changeByDiscountAmount' ? yup.number().min(0).test(yupTestMonetaryDecimals).required() : schema,
    ),
  changePercentage: yup
    .mixed<number | string | undefined>()
    .when('type', (type: PriceChangeFormValues['type'], schema: yup.BaseSchema) =>
      type === 'changeByPercentage'
        ? yup.number().positive().max(100).test(yupTestUpToDecimalPlaces(0)).required()
        : schema,
    ),
  applyDiscountPercentage: yup
    .mixed<number | string | undefined>()
    .when('type', (type: PriceChangeFormValues['type'], schema: yup.BaseSchema) =>
      type === 'changeByDiscountPercentage'
        ? yup.number().positive().max(100).test(yupTestUpToDecimalPlaces(0)).required()
        : schema,
    ),
});

export type ValidPriceChangeFormValues = yup.Asserts<typeof priceChangeValidationSchema>;
